import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { RachaPanel } from '../components/RachaPanel';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RefreshCw, Shield, Sparkles, Search,
  TrendingUp, ChevronDown, ChevronUp, Flame, Target, Zap, Crown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const BacktestPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/consensus/backtest`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 180000,
      });
      setData(res.data);
    } catch (e) {
      toast.error('Error cargando backtest');
    }
    setLoading(false);
  }, [token, data, loading]);

  const toggle = () => { setOpen(!open); if (!data) load(); };

  const g = data?.global;
  const baseline = data?.random_baseline || {};
  const lotteries = data?.lotteries || [];
  const displayed = showAll ? lotteries : lotteries.slice(0, 8);
  const lift = (a, b) => b > 0 ? (a / b).toFixed(1) : '0';

  return (
    <div className="mb-6 rounded-xl bg-slate-900/60 border border-purple-800/30 overflow-hidden"
      data-testid="consensus-backtest-panel">
      <button onClick={toggle}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-900/80 transition-colors"
        data-testid="consensus-backtest-toggle">
        <div className="flex items-center gap-3">
          <Target className="text-purple-400" size={20} />
          <div className="text-left">
            <h3 className="text-white font-bold text-sm">Rendimiento Real del Consenso</h3>
            <p className="text-slate-500 text-xs">Aciertos del Top 7 vs sorteos pasados</p>
          </div>
        </div>
        {open ? <ChevronUp className="text-slate-500" size={18} /> : <ChevronDown className="text-slate-500" size={18} />}
      </button>

      {open && (
        <div className="p-4 border-t border-slate-800/50 space-y-4" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          {loading && (
            <div className="flex items-center justify-center py-8 gap-2">
              <Loader2 className="animate-spin text-purple-400" size={20} />
              <span className="text-slate-500 text-sm">Calculando backtest…</span>
            </div>
          )}

          {g && !loading && (
            <>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'AL MENOS 1', rate: g.any_rate, base: baseline.any_rate || 9, color: 'emerald' },
                  { label: '2 DE 3', rate: g.two_rate, base: 0.5, color: 'amber' },
                  { label: 'TERMINAL', rate: g.term_any_rate, base: baseline.term_any_rate || 60, color: 'orange' },
                ].map((s) => {
                  const x = parseFloat(lift(s.rate, s.base));
                  const good = x >= 1.3;
                  return (
                    <div key={s.label} className={`p-3 rounded-lg bg-slate-800/40 border ${good ? 'border-purple-700/40' : 'border-slate-700/30'}`}>
                      <div className="text-slate-500 text-[10px] mb-0.5">{s.label}</div>
                      <div className="text-white font-black text-2xl">{s.rate}%</div>
                      <div className="flex items-center gap-1 text-[10px] mt-1">
                        <span className="text-slate-600">vs {s.base}%</span>
                        <span className={good ? 'text-purple-400 font-bold' : 'text-slate-500'}>{x}x</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-[10px] text-slate-500 flex items-center gap-2">
                <TrendingUp size={11} className="text-purple-500" />
                <span>
                  <strong className="text-slate-400">{g.draws_analyzed}</strong> sorteos en{' '}
                  <strong className="text-slate-400">{g.lotteries_evaluated}</strong> loterias — ventana {data.backtest_window}
                </span>
              </div>

              <div className="space-y-1">
                <div className="grid grid-cols-12 gap-2 px-2 text-[9px] text-slate-600 font-bold uppercase">
                  <div className="col-span-5">Loteria</div>
                  <div className="col-span-2 text-center">≥1</div>
                  <div className="col-span-2 text-center">2 de 3</div>
                  <div className="col-span-3 text-center">Terminal</div>
                </div>
                {displayed.map((l) => (
                  <div key={l.lottery_id} className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-slate-800/20 rounded-lg items-center text-xs"
                    data-testid={`consensus-row-${l.lottery_id}`}>
                    <div className="col-span-5 flex items-center gap-1.5 min-w-0">
                      <div className="w-1 h-4 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                      <span className="text-slate-300 truncate text-[11px]">{l.lottery_name}</span>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className={`font-bold text-[11px] ${l.any_rate >= 25 ? 'text-emerald-400' : l.any_rate >= 15 ? 'text-amber-400' : 'text-slate-500'}`}>
                        {l.any_rate}%
                      </span>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className={`font-bold text-[11px] ${l.two_rate > 0 ? 'text-amber-400' : 'text-slate-600'}`}>{l.two_rate}%</span>
                    </div>
                    <div className="col-span-3 text-center">
                      <span className={`font-bold text-[11px] ${l.term_rate >= 75 ? 'text-orange-400' : l.term_rate >= 60 ? 'text-amber-400' : 'text-slate-500'}`}>
                        {l.term_rate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {lotteries.length > 8 && (
                <button onClick={() => setShowAll(!showAll)}
                  className="w-full py-1.5 text-xs text-purple-400 hover:text-purple-300"
                  data-testid="consensus-toggle-all">
                  {showAll ? 'Mostrar menos' : `Ver todas (${lotteries.length - 8} mas)`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ConsensusCard = ({ pred }) => {
  const [expanded, setExpanded] = useState(false);
  const nums = pred.consensus_numbers || [];
  const conf = pred.confidence;
  const confColor = conf >= 75 ? 'text-emerald-400' : conf >= 60 ? 'text-amber-400' : 'text-slate-400';

  return (
    <div className="rounded-xl bg-slate-900/70 border border-slate-800/50 hover:border-purple-700/40 transition-all overflow-hidden"
      data-testid={`consensus-${pred.lottery_id}`}>
      <button className="w-full p-4 flex items-center gap-4 text-left" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <div className="w-2 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: pred.color }} />
            <h3 className="text-white font-bold text-sm truncate">{pred.lottery_name}</h3>
          </div>
          <div className="flex items-center gap-2 ml-4 text-xs">
            <span className="text-slate-500">{pred.draw_time}</span>
            <span className="text-slate-600">·</span>
            <span className="text-purple-400 font-bold">{pred.consensus_size} en interseccion</span>
            {pred.hot_terminals_consensus?.length > 0 && (
              <>
                <span className="text-slate-600">·</span>
                <span className="text-orange-300 flex items-center gap-1">
                  <Flame size={10} />
                  {pred.hot_terminals_consensus.map(t => `x${t}`).join(' ')}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {nums.slice(0, 7).map((n, i) => (
            <div key={`cn-${n.number}`}
              className={`rounded-xl flex items-center justify-center shadow-lg ${
                i === 0 ? 'w-12 h-12 bg-gradient-to-br from-purple-500 via-fuchsia-500 to-pink-500 shadow-fuchsia-500/30'
                : i === 1 ? 'w-10 h-10 bg-gradient-to-br from-purple-700/60 to-fuchsia-700/60'
                : 'w-8 h-8 bg-slate-800 border border-slate-700'
              }`}>
              <span className={`font-black text-white ${i === 0 ? 'text-base' : i === 1 ? 'text-sm' : 'text-xs'}`}>
                {String(n.number).padStart(2, '0')}
              </span>
            </div>
          ))}
        </div>
        <div className={`text-right ${confColor}`}>
          <div className="text-xl font-black">{conf}%</div>
          <div className="text-[9px] uppercase">puntaje</div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-800/50 space-y-3" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          {/* Hot terminals consensus */}
          {pred.hot_terminals_consensus?.length > 0 && (
            <div className="mt-3 p-2 rounded-lg bg-gradient-to-r from-orange-950/40 to-red-950/30 border border-orange-800/30">
              <div className="flex items-center gap-2 mb-1">
                <Flame size={12} className="text-orange-400" />
                <span className="text-orange-300 font-bold text-[11px]">Terminales Calientes (consenso de ambos motores)</span>
              </div>
              <div className="flex gap-2">
                {pred.hot_terminals_consensus.map((t) => (
                  <div key={`htc-${t}`} className="flex-1 p-2 rounded-lg bg-orange-500/15 border border-orange-500/30 text-center">
                    <div className="text-orange-300 font-black text-xl">x{t}</div>
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-[9px] mt-1">
                Ambos motores resaltan esta terminal; esto no indica una probabilidad de acierto.
              </p>
            </div>
          )}

          {/* All consensus numbers detail */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-500 text-[10px]">Top {nums.length} numeros de consenso (ranked):</span>
              <span className="text-slate-600 text-[9px]">P=Poderoso · M=Prometeo</span>
            </div>
            <div className="space-y-1">
              {nums.map((n, i) => (
                <div key={`det-${n.number}`} className="flex items-center gap-2 p-2 bg-slate-800/30 rounded-lg">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center font-black ${
                    i === 0 ? 'bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white text-base'
                    : 'bg-slate-800 text-slate-300 border border-slate-700 text-sm'
                  }`}>
                    {String(n.number).padStart(2, '0')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-300 text-[11px] truncate">{n.reason}</div>
                    <div className="flex gap-2 mt-0.5 text-[9px] text-slate-500">
                      <span>P: <strong className="text-amber-300">{n.poderoso_score}</strong></span>
                      <span>M: <strong className="text-cyan-300">{n.prometeo_score}</strong></span>
                    </div>
                  </div>
                  <span className="text-purple-300 font-bold text-xs">{n.consensus_score}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison */}
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div className="p-2 rounded-lg bg-amber-950/20 border border-amber-800/30">
              <div className="flex items-center gap-1 mb-0.5">
                <Crown size={10} className="text-amber-400" />
                <span className="text-amber-300 font-bold">Top 5 Poderoso</span>
              </div>
              <div className="text-white font-mono">
                {pred.poderoso_top?.map(n => String(n).padStart(2, '0')).join(' · ')}
              </div>
            </div>
            <div className="p-2 rounded-lg bg-cyan-950/20 border border-cyan-800/30">
              <div className="flex items-center gap-1 mb-0.5">
                <Zap size={10} className="text-cyan-400" />
                <span className="text-cyan-300 font-bold">Combo Prometeo</span>
              </div>
              <div className="text-white font-mono">
                {pred.prometeo_combo?.map(n => String(n).padStart(2, '0')).join(' · ')}
              </div>
            </div>
          </div>

          <div className="text-[10px] text-slate-500">
            Ultimo sorteo: {pred.last_draw?.map(n => String(n).padStart(2, '0')).join(' - ')}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ConsensoPage() {
  const { user, token } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/consensus/today`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
      });
      setPredictions(res.data.predictions || []);
    } catch (e) {
      toast.error('Error cargando Consenso');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const filtered = search
    ? predictions.filter(p => p.lottery_name?.toLowerCase().includes(search.toLowerCase()))
    : predictions;

  if (!user) return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
      <p className="text-slate-400">Inicia sesion</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0e1a]" data-testid="consenso-page">
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"><ArrowLeft size={16} /></Button>
            </Link>
            <div>
              <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 flex items-center gap-2">
                <Sparkles className="text-fuchsia-400" size={24} /> Consenso V4
              </h1>
              <p className="text-slate-500 text-sm">Poderoso V4 ∩ Prometeo V4 — los numeros donde dos motores coinciden</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            className="border-purple-700/40 text-purple-400 hover:text-white" data-testid="consensus-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        <RachaPanel engine="consenso" token={token} />

        <BacktestPanel token={token} />

        <div className="mb-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
          <div className="flex items-start gap-2 text-xs text-slate-400">
            <Shield size={14} className="text-purple-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p>
                <strong className="text-purple-300">Consenso V4</strong> intersecta los <strong className="text-amber-300">Top 10 de Poderoso</strong>
                {' '}con los <strong className="text-cyan-300">numeros fuertes de Prometeo</strong> y devuelve el Top 7 ranqueado por suma de scores.
                La coincidencia indica acuerdo entre modelos, no una probabilidad de ganar.
              </p>
              <p className="text-slate-500 text-[11px]">
                <strong className="text-emerald-400">Senal mas confiable:</strong> el puntaje interno ordena las selecciones; consulta el backtest histórico para medir resultados.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Buscar loteria..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="bg-slate-900/70 border-slate-800 text-white pl-10" data-testid="consensus-search" />
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-20 gap-3">
            <Loader2 className="animate-spin text-purple-400" size={36} />
            <p className="text-slate-500 text-sm">Calculando consenso de motores...</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="consensus-list">
            {filtered.map(p => <ConsensusCard key={p.lottery_id} pred={p} />)}
            {filtered.length === 0 && <div className="text-center py-12 text-slate-500">No se encontraron loterias</div>}
          </div>
        )}
      </div>
    </div>
  );
}
